import type { OpenSource } from '@parcel/analytics';
import type { TableFilter } from '@parcel/filters';
import {
  Button,
  DeleteViewDialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  ViewOptionsMenu,
  ViewTabs,
  WORKSPACE_VIEW_DELETE_DESCRIPTION,
} from '@parcel/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useRouterState } from '@tanstack/react-router';
import type { SortingState } from '@tanstack/react-table';
import { SlidersHorizontal } from 'lucide-react';
import * as React from 'react';
import { AccountsDataTable } from '../components/accounts/accounts-data-table.js';
import {
  ACCOUNTS_DEFAULT_SORTING,
  useAccountsTable,
} from '../components/accounts/use-accounts-table.js';
import { BrowseSummaryMeta } from '../components/data-table/browse-summary-meta.js';
import { ViewSelectionToolbar } from '../components/data-table/view-selection-toolbar.js';
import { PageHeader } from '../components/page-header.js';
import {
  type ApiAccountSummary,
  apiAccountToRow,
  useAccountBrowseSummary,
  useAccountSearchPreview,
  useWorkspaceAccounts,
} from '../lib/accounts.js';
import { useAuth } from '../lib/auth.js';
import { type ApiBrowseSummary, BROWSE_PAGE_SIZE } from '../lib/browse-query.js';
import {
  useEntityBrowseFirstRowsTiming,
  useEntityBrowseLaterPageSignals,
} from '../lib/entity-browse-observability.js';
import { filterEntriesKey } from '../lib/filter-params.js';
import { useAskFilters } from '../lib/filters-ask.js';
import { listTransitionKey } from '../lib/list-transition.js';
import { type AccountRow, MOCK_ACCOUNTS } from '../lib/mock-accounts.js';
import { useRecordDetail } from '../lib/record-detail.js';
import {
  DRAFT_VIEW_ID,
  DRAFT_VIEW_NAME,
  createDraftSavedView,
  isConfirmedViewName,
} from '../lib/saved-view-drafts.js';
import { useScore } from '../lib/scores.js';
import { isSupabaseConfigured } from '../lib/supabase.js';
import {
  applyAskWhere,
  hasTableFilter,
  makeTableSearchValidator,
  tableFilterEntries,
  useTableFilterSearch,
  useTableViewSearchParam,
} from '../lib/table-filter.js';
import { ACCOUNTS_BASE_PRESENTATION } from '../lib/table-views.js';
import {
  useEntityListViewScope,
  useWorkspaceTableViews,
} from '../lib/use-workspace-table-views.js';
import { runChunkedMembership, selectionMap } from '../lib/view-selection-state.js';
import { useWatchSelection } from '../lib/watch-selection.js';
import { useWhereOptions } from '../lib/where-options.js';
import {
  type WorkspaceView,
  useFavoriteViewMutations,
  useFavoriteViews,
  useWorkspaceViewMutations,
  useWorkspaceViews,
} from '../lib/workspace-views.js';
import { getWorkspace, getWorkspaceSlugFromPath } from '../lib/workspaces.js';

const SEARCH_DEBOUNCE_MS = 250;

export const Route = createFileRoute('/accounts')({
  component: AccountsPage,
  validateSearch: makeTableSearchValidator({ view: 'uuid' }),
});

export function AccountsPage() {
  const queryClient = useQueryClient();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { session } = useAuth();
  const userId = session?.user.id;
  const workspaceSlug = getWorkspaceSlugFromPath(pathname);
  const workspaceQuery = useQuery({
    queryKey: ['workspace', workspaceSlug],
    enabled: Boolean(workspaceSlug) && isSupabaseConfigured,
    queryFn: () => getWorkspace(workspaceSlug ?? ''),
  });
  const workspaceId = workspaceQuery.data?.id;

  const { filter: filterState, setFilter: setFilterState } = useTableFilterSearch('account');
  const { activeViewId: urlViewId, setActiveViewId, urlHasFilterKeys } = useTableViewSearchParam();
  const filterEntries = React.useMemo(() => tableFilterEntries(filterState), [filterState]);
  // Mirror filterState so the async ask handler merges into the latest state.
  const filterStateRef = React.useRef(filterState);
  filterStateRef.current = filterState;
  const { mutateAsync: askFilters } = useAskFilters();
  const handleAsk = React.useCallback(
    async (query: string) => {
      if (!workspaceId) throw new Error('workspace not loaded');
      const result = await askFilters({ workspaceId, entity: 'account', query });
      setFilterState(applyAskWhere(filterStateRef.current, result));
      return result;
    },
    [askFilters, workspaceId, setFilterState],
  );

  const viewsQuery = useWorkspaceViews(workspaceId, 'account');
  const viewsListStatus = viewsQuery.isPending
    ? 'pending'
    : viewsQuery.isError
      ? 'error'
      : 'success';
  const listScope = useEntityListViewScope({
    views: viewsQuery.data,
    urlViewId,
    dataset: 'account',
    listStatus: viewsListStatus,
    urlHasFilterKeys,
    filterState,
  });

  const browseMode = true;
  const [sorting, setSorting] = React.useState<SortingState>(ACCOUNTS_DEFAULT_SORTING);
  const summaryScopeKey = React.useMemo(
    () =>
      `${workspaceId ?? ''}|${urlViewId ?? ''}|${filterEntriesKey(filterEntries)}|${listScope.staticViewId ?? ''}`,
    [filterEntries, listScope.staticViewId, urlViewId, workspaceId],
  );
  const summaryQuery = useAccountBrowseSummary(workspaceId, filterEntries, listScope.staticViewId, {
    enabled: listScope.ready,
  });
  const [acceptedSummary, setAcceptedSummary] = React.useState<{
    workspaceId: string;
    scopeKey: string;
    summary: ApiBrowseSummary;
  } | null>(null);
  React.useEffect(() => {
    setAcceptedSummary((current) => {
      if (!current || current.workspaceId === workspaceId) return current;
      return null;
    });
  }, [workspaceId]);
  React.useEffect(() => {
    if (!workspaceId || !listScope.ready || !summaryQuery.data) return;
    setAcceptedSummary((current) => {
      if (current?.scopeKey === summaryScopeKey) return current;
      return { workspaceId, scopeKey: summaryScopeKey, summary: summaryQuery.data };
    });
  }, [listScope.ready, summaryQuery.data, summaryScopeKey, workspaceId]);
  const accepted =
    listScope.ready && acceptedSummary?.scopeKey === summaryScopeKey
      ? acceptedSummary.summary
      : undefined;

  const accountsQuery = useWorkspaceAccounts(workspaceId, filterEntries, listScope.staticViewId, {
    enabled: listScope.ready,
    browseMode,
    sorting,
  });

  // Identity of the active view + filters. A view/filter change navigates the
  // URL, and the list query only re-keys once that navigation commits (a few
  // hundred ms later); until then the old rows sit on screen with no loading
  // state. Flip a flag synchronously on the change so the skeleton shows
  // immediately, then clear it once this identity moves off the pre-change value
  // AND the query settles. The view id MUST be part of the identity: two views
  // can share a filter (e.g. Architects vs Architects New), and without it a
  // switch between them never registers as changed and the skeleton sticks.
  const listKey = listTransitionKey(urlViewId, filterEntriesKey(filterEntries));
  const [isListTransitioning, setIsListTransitioning] = React.useState(false);
  const transitionFromKeyRef = React.useRef(listKey);
  const beginListTransition = React.useCallback(() => {
    transitionFromKeyRef.current = listKey;
    setIsListTransitioning(true);
  }, [listKey]);
  React.useEffect(() => {
    if (
      isListTransitioning &&
      listKey !== transitionFromKeyRef.current &&
      !accountsQuery.isFetching &&
      !accountsQuery.isPlaceholderData
    ) {
      setIsListTransitioning(false);
    }
  }, [isListTransitioning, listKey, accountsQuery.isFetching, accountsQuery.isPlaceholderData]);
  const handleFilterStateChange = React.useCallback(
    (next: TableFilter) => {
      // A refused or unchanged filter never moves the list, so it starts no loading state.
      if (setFilterState(next)) beginListTransition();
    },
    [beginListTransition, setFilterState],
  );
  const icpScore = useScore(workspaceId, 'icp').data;
  const whereOptions = useWhereOptions(workspaceId, 'account');

  const [recordInput, setRecordInput] = React.useState('');
  const [debouncedRecordQuery, setDebouncedRecordQuery] = React.useState('');
  React.useEffect(() => {
    const handle = window.setTimeout(
      () => setDebouncedRecordQuery(recordInput.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(handle);
  }, [recordInput]);
  const previewQuery = useAccountSearchPreview(
    workspaceId,
    debouncedRecordQuery,
    filterEntries,
    listScope.staticViewId,
  );
  const previewById = React.useMemo(() => {
    const map = new Map<string, ApiAccountSummary>();
    for (const account of previewQuery.data?.accounts ?? []) {
      map.set(account.id, account);
    }
    return map;
  }, [previewQuery.data]);
  const previewRecords = React.useMemo(
    () =>
      (previewQuery.data?.accounts ?? []).map((account) => ({
        id: account.id,
        label: account.name,
        sublabel: account.city && account.city !== '—' ? account.city : undefined,
      })),
    [previewQuery.data],
  );

  const accounts = React.useMemo<AccountRow[]>(() => {
    if (!isSupabaseConfigured) return MOCK_ACCOUNTS;
    return (accountsQuery.data?.accounts ?? []).map(apiAccountToRow);
  }, [accountsQuery.data]);
  const [browseRefreshRevision, setBrowseRefreshRevision] = React.useState(0);
  const { table } = useAccountsTable(accounts, {
    browseMode,
    sorting,
    onSortingChange: setSorting,
    icpScore,
  });
  const { record, open: openRecord, close: closeRecord } = useRecordDetail();
  const openAccountId = record?.type === 'account' ? record.id : null;
  // A null open only comes from the table dropping the row on a scope change: keep that scope.
  const handleOpenAccount = React.useCallback(
    (account: AccountRow | null, source: OpenSource = 'table') => {
      if (account) openRecord({ type: 'account', id: account.id }, { seed: account, source });
      else closeRecord({ unwind: false });
    },
    [closeRecord, openRecord],
  );
  const tableScopeKey = React.useMemo(
    () =>
      `${workspaceId ?? ''}|${urlViewId ?? ''}|${filterEntriesKey(filterEntries)}|${listScope.staticViewId ?? ''}|${sorting.map((sort) => `${sort.id}:${sort.desc ? 'desc' : 'asc'}`).join(',')}|${browseRefreshRevision}`,
    [browseRefreshRevision, filterEntries, listScope.staticViewId, sorting, urlViewId, workspaceId],
  );
  useEntityBrowseFirstRowsTiming(
    'account',
    browseMode,
    tableScopeKey,
    accounts.length,
    accountsQuery.isLoading,
  );
  const loadedPageCount = accounts.length === 0 ? 0 : Math.ceil(accounts.length / BROWSE_PAGE_SIZE);
  const { wrapFetchNextPage } = useEntityBrowseLaterPageSignals(
    'account',
    browseMode,
    accountsQuery.isFetchNextPageError ?? false,
    loadedPageCount,
  );
  const instrumentedFetchNextPage = React.useMemo(
    () => wrapFetchNextPage(accountsQuery.fetchNextPage ?? (async () => undefined)),
    [accountsQuery.fetchNextPage, wrapFetchNextPage],
  );
  const handleBrowseRefresh = React.useCallback(() => {
    queryClient.removeQueries({
      queryKey: [
        'accounts-browse',
        workspaceId,
        filterEntriesKey(filterEntries),
        listScope.staticViewId ?? null,
      ],
    });
    table.resetRowSelection();
    setBrowseRefreshRevision((revision) => revision + 1);
    if (workspaceId && summaryQuery.data) {
      setAcceptedSummary({
        workspaceId,
        scopeKey: summaryScopeKey,
        summary: summaryQuery.data,
      });
    }
    void summaryQuery.refetch();
  }, [
    filterEntries,
    listScope.staticViewId,
    queryClient,
    summaryQuery,
    summaryScopeKey,
    table,
    workspaceId,
  ]);

  const savedViews = useWorkspaceTableViews({
    workspaceId,
    dataset: 'account',
    table,
    basePresentation: ACCOUNTS_BASE_PRESENTATION,
    filterState,
    setFilterState,
    activeViewId: urlViewId,
    setActiveViewId,
    urlHasFilterKeys,
  });
  const favoritesQuery = useFavoriteViews(workspaceId, userId);
  const favoriteMutations = useFavoriteViewMutations(workspaceId, userId);
  const activeViewIsFavorite = Boolean(
    savedViews.activeView &&
      (favoritesQuery.data?.some((view) => view.id === savedViews.activeView?.id) ??
        savedViews.activeView.is_favorite),
  );
  const toggleFavorite = (view: WorkspaceView) => {
    const isFavorite =
      favoritesQuery.data?.some((favorite) => favorite.id === view.id) ?? Boolean(view.is_favorite);
    if (isFavorite) favoriteMutations.unfavorite.mutate(view.id);
    else favoriteMutations.favorite.mutate({ view_id: view.id });
  };
  const [editingViewId, setEditingViewId] = React.useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [editingValue, setEditingValue] = React.useState('');
  const [draftView, setDraftView] = React.useState<ReturnType<typeof createDraftSavedView> | null>(
    null,
  );
  const visibleViews = [
    ...savedViews.views.map((view) => ({
      id: view.id,
      name: view.name,
      // The system Following tabs say so in their name; only a followed list View gets the star.
      followed: view.watched && !view.system_kind,
    })),
    ...(draftView ? [{ id: draftView.id, name: draftView.name }] : []),
  ];
  const activeViewId = draftView ? draftView.id : savedViews.activeViewId;
  const isViewTabsDirty = draftView ? false : savedViews.canSaveAsNew;
  // Skeleton on: initial load, the keepPreviousData fetch window, AND the
  // click->navigation gap before the query re-keys (isListTransitioning).
  const isListLoading =
    accountsQuery.isLoading || accountsQuery.isPlaceholderData || isListTransitioning;

  const startNewView = () => {
    const view = createDraftSavedView({
      columnFilters: [],
      sorting: [],
      columnVisibility: {},
      globalFilter: '',
    });
    setDraftView(view);
    setEditingViewId(DRAFT_VIEW_ID);
    setEditingValue(DRAFT_VIEW_NAME);
  };

  const finishEditingView = () => {
    const nextName = editingValue.trim();
    if (draftView && editingViewId === DRAFT_VIEW_ID) {
      if (isConfirmedViewName(nextName)) {
        void savedViews.saveAsNew(nextName);
      }

      setDraftView(null);
      setEditingViewId(null);
      setEditingValue('');
      return;
    }

    if (editingViewId !== savedViews.activeViewId) {
      setEditingViewId(null);
      setEditingValue('');
      return;
    }

    if (nextName) {
      void savedViews.renameView(nextName);
    }

    setEditingViewId(null);
    setEditingValue('');
  };

  const cancelEditingView = () => {
    setDraftView(null);
    setEditingViewId(null);
    setEditingValue('');
  };

  const applyBaseView = () => {
    if (urlViewId !== null || hasTableFilter(filterState)) beginListTransition();
    setDraftView(null);
    savedViews.applyBaseView();
  };

  const applySavedView = (viewId: string) => {
    if (viewId !== urlViewId) beginListTransition();
    setDraftView(null);
    savedViews.applyView(viewId);
  };

  const viewMutations = useWorkspaceViewMutations(workspaceId, 'account');
  const followSelection = useWatchSelection(workspaceId, 'account');
  const activeView = savedViews.activeView;
  const toggleWatch =
    activeView?.view_type === 'static' && !activeView.system_kind
      ? () => viewMutations.upsert.mutate({ id: activeView.id, watched: !activeView.watched })
      : undefined;
  const selectedIds = table.getSelectedRowModel().rows.map((row) => row.original.id);
  const staticViews = React.useMemo(
    () => savedViews.views.filter((view) => view.view_type === 'static'),
    [savedViews.views],
  );
  const activeStaticView =
    savedViews.activeView?.view_type === 'static' ? savedViews.activeView : null;

  return (
    <main className="flex min-h-0 min-w-0 flex-1 flex-col">
      <PageHeader
        title="Accounts"
        meta={
          isSupabaseConfigured ? (
            <BrowseSummaryMeta
              summaryQuery={summaryQuery}
              baselineDataVersion={accepted?.data_version ?? null}
              onRefresh={handleBrowseRefresh}
            />
          ) : null
        }
      />

      <ViewTabs
        views={visibleViews}
        activeViewId={activeViewId}
        isDirty={isViewTabsDirty}
        editingViewId={editingViewId}
        editingValue={editingValue}
        onEditingValueChange={setEditingValue}
        onCommitEditingView={finishEditingView}
        onCancelEditingView={cancelEditingView}
        onSelectBase={applyBaseView}
        onSelectView={applySavedView}
        onSaveAsNew={startNewView}
        actions={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 gap-2 rounded-full">
                <SlidersHorizontal className="h-4 w-4" />
                <span className="inline-flex items-center gap-2">
                  Views
                  {savedViews.activeView && savedViews.isDirty ? (
                    <span className="h-1.5 w-1.5 rounded-full bg-destructive" aria-hidden />
                  ) : null}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-56"
              onCloseAutoFocus={(event) => event.preventDefault()}
            >
              <ViewOptionsMenu
                hasActiveView={Boolean(savedViews.activeView)}
                isDirty={savedViews.isDirty}
                canSaveAsNew={savedViews.canSaveAsNew}
                onSave={savedViews.saveCurrent}
                onSaveAsNew={startNewView}
                isFavorite={activeViewIsFavorite}
                onToggleFavorite={() => {
                  const view = savedViews.activeView;
                  if (view) toggleFavorite(view);
                }}
                onRename={() => {
                  if (!savedViews.activeView) return;
                  setDraftView(null);
                  setEditingViewId(savedViews.activeView.id);
                  setEditingValue(savedViews.activeView.name);
                }}
                onDelete={() => setDeleteDialogOpen(true)}
                isWatched={Boolean(activeView?.watched)}
                onToggleWatch={toggleWatch}
                systemView={Boolean(activeView?.system_kind)}
                followUnavailable={
                  activeView && activeView.view_type !== 'static'
                    ? 'Only list views can be followed for now'
                    : undefined
                }
              />
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />

      <AccountsDataTable
        table={table}
        browseMode={browseMode}
        scopeKey={tableScopeKey}
        filterState={filterState}
        onFilterStateChange={handleFilterStateChange}
        options={whereOptions}
        openAccountId={openAccountId}
        onOpenAccount={handleOpenAccount}
        isLoading={isSupabaseConfigured && isListLoading}
        isError={isSupabaseConfigured && accountsQuery.isError}
        hasNextPage={accountsQuery.hasNextPage ?? false}
        isFetchingNextPage={accountsQuery.isFetchingNextPage ?? false}
        isFetchNextPageError={accountsQuery.isFetchNextPageError ?? false}
        fetchNextPage={instrumentedFetchNextPage}
        summaryTotal={accepted?.total ?? summaryQuery.data?.total ?? null}
        recordMatches={previewRecords}
        onRecordInputChange={(text) => setRecordInput(text)}
        onSelectRecord={(id) => {
          const preview = previewById.get(id);
          if (preview) {
            handleOpenAccount(apiAccountToRow(preview), 'search');
            return;
          }
          const loaded = accounts.find((account) => account.id === id);
          if (loaded) handleOpenAccount(loaded, 'search');
        }}
        onAsk={workspaceId ? handleAsk : undefined}
        selectionActive={selectedIds.length > 0}
        selectionToolbar={
          <ViewSelectionToolbar
            dataset="account"
            selectedIds={selectedIds}
            staticViews={staticViews}
            activeStaticView={activeStaticView}
            onAdd={(viewId) =>
              runChunkedMembership(selectedIds, (chunk) =>
                viewMutations.addRecords.mutateAsync({ viewId, recordIds: chunk }),
              )
            }
            onCreate={async (name) => {
              const created = await viewMutations.staticCreate.mutateAsync({
                name,
                dataset: 'account',
                initial_record_ids: selectedIds.slice(0, 2000),
              });
              return { view: created.view, outcomes: created.results };
            }}
            onRemove={
              activeStaticView
                ? () =>
                    runChunkedMembership(selectedIds, (chunk) =>
                      viewMutations.removeRecords.mutateAsync({
                        viewId: activeStaticView.id,
                        recordIds: chunk,
                      }),
                    )
                : undefined
            }
            onFollow={() => followSelection(selectedIds)}
            onOpenView={(view) => {
              setDraftView(null);
              savedViews.applyView(view.id);
            }}
            onSelectionChange={(ids) => table.setRowSelection(selectionMap(ids))}
          />
        }
      />

      <DeleteViewDialog
        open={deleteDialogOpen}
        viewName={savedViews.activeView?.name ?? 'this View'}
        description={WORKSPACE_VIEW_DELETE_DESCRIPTION}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={() => {
          setDeleteDialogOpen(false);
          void savedViews.deleteView();
        }}
      />
    </main>
  );
}
